/**
 * An engine that is able to process the steps outlined in the page template
 * that is used for the EDC Portal and Mobile Application
 *
 * TODO: This class needs to be aware of the current user for permissions.
 *
 */

// Library for Events that are strongly typed
// see https://github.com/vitaly-t/sub-events
import { SubEvent } from 'sub-events';

import { PageDataType } from './PageTypes';
import { MetaType } from './PageTypes';
import { PageStepType } from './PageTypes';
import { MetaRefreshType } from './PageTypes';

import { DataServices } from '../utils/DataServices';
import { DataTools } from '../utils/DataTools';

import * as StepTypes from './StepTypes';
import { PageRenderBase, PageRenderSilent } from './PageRenderBase';

import { LogicEngine } from '../LogicEngine/LogicEngine';
import { StepSetValue } from './StepSetValue';
import { StepLookup } from './StepLookup';
import { DataQuery } from '../utils/DataQuery';
import { DataRecord } from '../utils/DataRecord';
import { Participant } from '../Participant/Participant';
import { StringOptions } from '../utils/StringOptions';

import { BranchManager } from './CodeBranch';
import { DataFormatter } from '../dataformats/DataFormatter';
import { FieldError } from '../utils/DataDictionary';
import { PageManager } from './PageManager';
import { NotificationManager } from '../utils/NotificationManager';
import { RemoteAgent } from '../RemoteAgent/RemoteAgent';
import { AuditLog } from '../Participant/AuditLog';
import { StepLoop } from './StepLoop';
import { StepChart } from './StepChart';
import { textChangeRangeIsUnchanged } from 'typescript';

/** Internal interface for callbacks to process steps */
interface StepFunction
{
	(step: PageStepType): Promise<boolean>;
}

interface stepLoopData
{
	loopType: 'counter' | 'list';
	variableName: string;
	condition: string;

	// For Counter
	startValue?: number;
	increase?: number;
}

export class PageEngine
{
	rawConfig: PageDataType;
	renderer: PageRenderBase | undefined;
	logic: LogicEngine;
	sendUpdates: boolean = true;

	/**
	 * Event that Fired when the page is initialized the first time.
	 * Called with the page meta data
	 */
	evInitializePage: SubEvent<MetaType> = new SubEvent();

	/**
	 * Event that is fired when the page is done processing.
	 * Called with the page meta data
	 */
	evFinishedPage: SubEvent<MetaType> = new SubEvent();

	/**
	 * Event that is fired when certain aspects of data may need to be refreshed
	 * due to a record being added.
	 */
	evForceRefresh: SubEvent<MetaRefreshType> = new SubEvent();


	evForceRedirect: SubEvent<string> = new SubEvent();

	/**
	 * Generic event that fires before each step is processed.
	 * When possible, it's better to use type specific events.
	 */
	evStep: SubEvent<PageStepType> = new SubEvent();
	evStepEnd: SubEvent<PageStepType> = new SubEvent();

	/** List of known steps we can process */
	private stepList: { [stepName: string]: StepFunction } = {};

	/** Keep track of the current group we are using so that
	 * we can close it correctly and keep it alive so it can be
	 * used for storage while processing the group elements.
	 */
	private currentGroup: StepTypes.Group | undefined;
	private currentTable: StepTypes.Table | undefined;
	private currentTabs: StepTypes.Tabs | undefined;
	private currentForm: StepTypes.Form | undefined;
	private currentList: StepTypes.List | undefined;
	private currentRecord: StepTypes.Record | undefined;
	private currentSection: StepTypes.Section | undefined;
	private currentLoop: StepTypes.Loop | undefined;
	private currentTest: StepTypes.Test | undefined;
	private currentChart: StepChart | undefined;
	private currentDataLoop: StepTypes.Loop | undefined;
	private currentWindow: StepTypes.Window | undefined;

	// Reference to a single notification manager
	notificationManager: NotificationManager = new NotificationManager();

	private currentRecords: { [tableName: string]: DataRecord } = {};

	private currentListItems: PageStepType[] = [];
	private currentListOptions: PageStepType[] = [];
	private currentListActive: boolean = false;
	private currentChartActive: boolean = false;

	// Autosave "Participant Set" values
	private participantSaveTimer: NodeJS.Timer | number | undefined;
	private participantAutosaveTime = 200;

	protected macroList: { [name: string]: PageStepType[] } = {};

	// Manager all the code branches for if, else, section, etc..
	private branchManager: BranchManager = new BranchManager();

	constructor(rawData: PageDataType)
	{
		this.rawConfig = rawData;
		this.logic = new LogicEngine();

		this.logic.assign('current_route', rawData.meta.route);
		this.notificationManager.logicRef = this.logic;

		/*  Basic UI Elements */
		this.register('text', this.stepText);

		this.register('trace', this.stepTrace);

		this.register('button', this.stepButton);

		this.register('break', this.stepBreak);
		this.register('br', this.stepBreak);

		/*  Logic commands */
		this.register('skipif', this.stepSkipIf);
		this.register('setvalue', this.stepSetValue);
		this.register('set', this.stepSetValue);
		// this.register("set", this.stepSet);

		this.register('lookup', this.stepLookup);

		/*  Dashboard specific elements */
		this.register('dashstat', this.stepDashStat);
		this.register('dashbutton', this.stepDashButton);

		this.register('task', this.stepTask);

		this.register('audit', this.stepAudit);

		this.register('record', this.stepRecord);

		this.register('participant', this.stepParticipant);

		this.register('redirect', this.stepRedirect);
		this.register('include', this.stepInclude);

		/* Group related Dashboard commands */
		this.register('groupend', this.stepGroupEnd);
		this.register('endgroup', this.stepGroupEnd);

		this.register('groupstart', this.stepGroupStart);
		this.register('startgroup', this.stepGroupStart);
		this.register('begingroup', this.stepGroupStart);

		this.register('begin code', this.internalBeginCode);
		this.register('end code', this.internalEndCode);

		this.register('list', this.stepStartList);
		this.register('beginlist', this.stepStartList);
		this.register('endlist', this.stepEndList);
		this.register('option', this.stepListOption);

		this.register('beginsection', this.stepStartSection);
		this.register('startsection', this.stepStartSection);
		this.register('section', this.stepStartSection);
		this.register('endsection', this.stepEndSection);
		this.register('stopsection', this.stepEndSection);

		/** Relatest to testing */
		this.register("begintest", this.stepBeginTest);
		this.register("endtest", this.stepEndTest);
		this.register('test', this.stepOneTest);

		/*  Related to tables */
		this.register('begintable', this.stepBeginTable);
		this.register('tablebegin', this.stepBeginTable);
		this.register('table', this.stepBeginTable);
		this.register('endtable', this.stepEndTable);
		this.register('tableend', this.stepEndTable);
		this.register('column', this.stepColumn);

		/* Related to loops */
		this.register('beginloop', this.stepBeginLoop);
		this.register('endloop', this.stepEndLoop);

		/** Import data from a dataset */
		this.register('import', this.stepImport);

		this.register('begintabs', this.stepBeginTabs);
		this.register('tabsbegin', this.stepBeginTabs);
		this.register('endtabs', this.stepEndTabs);
		this.register('tabsend', this.stepEndTabs);
		this.register('tab', this.stepTab);

		this.register('beginform', this.stepBeginForm);
		this.register('formbegin', this.stepBeginForm);
		this.register('endform', this.stepEndForm);
		this.register('formend', this.stepEndForm);
		this.register('field', this.stepField);

		this.register('if', this.startConditionalIf);
		this.register('endif', this.endConditionalIf);
		this.register('else', this.startConditionalElse);

		this.register('beginmacro', this.internalBeginMacro);
		this.register('startmacro', this.internalBeginMacro);
		this.register('endmacro', this.internalEndMacro);
		this.register('stopmacro', this.internalEndMacro);
		this.register('macro', this.internalRunMacro);

		this.register('comment', this.stepComment);
		this.register('todo', this.stepComment);

		this.register('debug', this.stepDebug);

		this.register('conditional', this.stepConditional);
		this.register('condition', this.stepConditional);

		this.register("notification", this.stepNotification);

		this.register("exit", this.stepExit);

		this.register("inc", this.stepLogicIncrement);
		this.register("increment", this.stepLogicIncrement);
		this.register("incr", this.stepLogicIncrement);

		/** Chart Related Commands */
		this.register("startchart", this.stepBeginChart);
		this.register("beginchart", this.stepBeginChart);
		this.register("endchart", this.stepEndChart);
		this.register("chartopion", this.stepChartOption);

		this.register("begindata", this.stepBeginData);
		this.register("enddata", this.stepEndData);
		this.register("mapdata", this.stepMapData);

		/** Window Related Commands */
		this.register('beginwindow', this.stepBeginWindow);
		this.register('windowbegin', this.stepBeginWindow);
		this.register('endwindow', this.stepEndWindow);
		this.register('windowend', this.stepEndWindow);
		this.register('window', this.stepWindow);
	}

	/** Helper function that will associate a command with a callback function */
	register(stepName: string, callback: StepFunction)
	{
		stepName = stepName.toLowerCase().replace(' ', '');
		this.stepList[stepName] = callback.bind(this);
	}

	/**
	 * Returns true if the page is valid
	 */
	isValid(): boolean
	{
		if (!this.rawConfig) return false;
		if (!this.rawConfig.tableName) return false;
		if (this.rawConfig.tableName.length === 0) return false;
		if (this.rawConfig.tableName === "") return false;

		return true;
	}

	/** Helper function to load the contents of something that is in the CDN into a record. */
	async loadCdnIntoRecord(url: string, target: string)
	{
		// Reset the existing currentRecord to a blank record
		let dataSet = {};
		if (this.currentRecord && this.currentRecord.record)
		{
			Object.assign(dataSet, this.currentRecord.record.data);
		}

		// If not specified or illegal, the target is 'file';
		target = DataTools.internalValidateString(target) || 'file';

		// remove the CDN path reference
		url = url.replace("/cdn/", "").trim();

		if (LogicEngine.debugOutput)
			console.log("LogicEngine LoadCDN source=", url, "Target=", target);

		let value = await DataServices.instance().internalGetCdnFile<string>(url);

		if (!value)
		{
			console.error("LogicEngine LoadCDN Failed to get content from ", url);
			this.logic.assign(target + "_raw", "");
			this.logic.assign(target, "");
		} else
		{
			// TODO:  Check the file type.  If it's .xml or .json then process it different
			// assume text or html or something else for now and insert into the variable "target"
			this.logic.assign(target + "_raw", value);
			this.logic.assign(target, this.logic.processTextReplacement(value));
		}

	}

	/**
	 * execute a REST API and store the results in the record
	 * The values already set into the record using "Record set" will be passed as
	 * values to the REST Api.
	 */
	async loadApiIntoRecord(url: string, method: string)
	{
		let dataSet = {};
		if (this.currentRecord && this.currentRecord.record)
		{
			Object.assign(dataSet, this.currentRecord.record.data);
		}

		console.log('PageEngine calling rest api:', url, ' with queryString=', dataSet);

		let callMethod = 'get';
		let result: any = {};

		method = method.toLowerCase();
		if (method === 'post')
		{
			result = await DataServices.instance().makeApiCall(url, dataSet, 'post');
		} else
		{
			result = await DataServices.instance().makeApiCall(url, dataSet, 'get');
		}

		if (result && result.isError)
		{
			console.log('Warning: PageEngine loadApiIntoRecord error in call', {
				call: url,
				method: method,
				result: result,
			});

			this.logic.assign('error', result.errorMessage);
			this.logic.assign('success', false);
		} else
		{
			/** Copy the values from result to the record set that is loaded */
			if (this.currentRecord && this.currentRecord.record)
			{
				for (let key of Object.keys(result.result))
				{
					this.currentRecord.record.data[key] = result.result[key];
				}
			}

			/* Assign a copy of the values to the logic engine */
			if (this.logic)
			{
				this.logic.import(result.result);
			}
		}

		return true;
	}

	/**
	 * Load a record from the database and populate the
	 * logic engine with those values.
	 *
	 * @param tableName
	 * @param id
	 */
	async loadRecord(tableName: string, id: string): Promise<boolean>
	{
		if (LogicEngine.debugOutput)
			console.log("PageEngine Loading Record table=", tableName, "id=", id);

		if (tableName === 'Participant')
		{
			/** Special case for participants so that we load and validate aspects of the particiapnt
			 * at the same time.  Also supports loading by uuid and id
			 */
			let p = new Participant();
			if (await p.loadById(id))
			{
				if (p.participantDataRecord)
				{
					await p.participantDataRecord.fixRecord();
					this.logic.import(p.participantDataRecord.data);
					this.logic.assign(tableName + "_id", p.participantDataRecord.data.id);
					this.logic.assign('participant_id', p.participantDataRecord.data.id);
					this.logic.assign('participant_uuid', p.participantDataRecord.data.uuid);
					this.currentRecords[tableName] = p.participantDataRecord;
					await this.currentRecords[tableName].updateCalculations(this.logic);
					return true;
				} else
				{
					console.log("PageEngine Loader Warning, missing record for Participant", id);
				}
			}
		}

		const dq = DataQuery.instance();
		const record = await dq.executeQueryById(tableName, id);
		if (record)
		{
			this.logic.import(record.data);
			this.currentRecords[tableName] = record;
			await this.currentRecords[tableName].fixRecord();
			await this.currentRecords[tableName].updateCalculations(this.logic);

			this.logic.assign(tableName + "_id", record.data.id);
			this.logic.assign(tableName + "_id".toLowerCase(), record.data.id);
			return true;
		} else
		{
			console.warn('PageEngine LoadRecord failed to load table=', tableName, 'id=', id);
		}

		return false;
	}

	/**
	 * Check to see if a group list from view Groups of Edit Groups
	 * contains a list and if so, does the current user fit.
	 *
	 * @param groupList string or undefined list of groups with a comma.
	 */
	internalCheckPermission(groupList: string | undefined): boolean
	{
		if (!groupList) return true;
		if (groupList.trim().length === 0) return true;

		const result = this.logic.fnHasGroup(groupList);
		return result;
	}

	/** Called when all items on the page have finished processing */
	async processEndOfPage(list: PageStepType[]) { }

	/** Process a list of commands */
	async processPageItems(list: PageStepType[])
	{
		//  Initialize the nocache value
		this.logic.assign("nocache", "nocache=" + new Date().getTime().toString(22));

		//  Initialize a random string value for XX option
		let letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
		let code = "XX";
		for (let x = 0; x < 8; x++)
		{
			let num = Math.floor(Math.random() * letters.length);
			code += letters.charAt(num);
		}

		this.logic.assign("xxcode", code);

		//  Assign row number if they are not there.  Older versions don't have row numbers.
		let rowNum = 0;
		for (let item of list)
		{
			++rowNum;
			if (item && !item._row)
				item._row = rowNum;
		}

		// Process each item in the list.
		for (let item of list)
		{
			try
			{
				// Update logic engine with current line number for debugging
				if (!item._row)
					item._row = rowNum++;

				if (item._row)
					LogicEngine.currentLineNumber = item._row;

				//  Set for debug purposes
				LogicEngine.currentCommandText = "Row: " + item._row + " Command: " + item.step_type + " Option: " + item.step_option + " Text: " + item.text_en;

				// Convert the name from some human text in excel to a lowercase
				// and remove spaces allowing more flexibility for dumb human.
				if (!item.step_type || item.step_type === '') continue;

				const stepName = item.step_type.toLowerCase().replace(' ', '');
				item.step_type = stepName;

				if (!this.internalCheckPermission(item.view_groups))
				{
					console.log(
						'Debug Info, command was skipped due to permissions.  Required is: ',
						item.view_groups,
						' for ',
						item.step_type,
					);
					continue;
				}

				/* Debug current command */
				// console.log("CHECK COMMAND:", item, {
				//     branchCurrent: this.branchCurrent ? "Yes" : "No",
				//     currentList : this.currentForm ? "Yes" : "No",
				//     currentListActive : this.currentListActive ? "Yes" : "No"
				// });

				/* If we are working on a code branch such as a macro, list,
				if statement or something else, we just store the commands but
				don't execute them yet */
				if (this.branchManager.hasCurrent() && !this.branchManager.isAllowList(stepName))
				{
					this.branchManager.addItem(item);
					continue;
				}

				if (this.currentList && this.currentListActive)
				{
					if (stepName === 'option')
					{
						this.currentListOptions.push(item);
						continue;
					} else if (stepName !== 'endlist')
					{
						this.currentListItems.push(item);
						continue;
					}
				}

				// Check to see if there is an "if" conditions for the step, don't process steps that
				// fail to check the if condition.
				if (!this.validateStepIfCondition(item))
					continue;

				// Call an event for all items for debugging or however
				// this needs to be used.
				this.evStep.emit(item);

				if (LogicEngine.debugWebdriver)
					console.log("STEP:", DataTools.toJSON(item));

				// Set a default handler if we haven't registered one for this type
				// not all steps need a custom handler.
				if (!this.stepList[stepName]) this.stepList[stepName] = this.stepDefault.bind(this);

				// Call the handler using async await as some handlers
				// may need to make database calls or other things in order to continue.
				await this.stepList[stepName](item);

				// End of step marker
				this.evStepEnd.emit(item);
			} catch (e)
			{
				console.error('Error in ProcessPageItems in item:', item);
				DataTools.reportException('ProcessPageItem', e);
			}
		}
	}

	/**
	 * Import the commands from one pageEngine into this
	 * pageEngine at the top.  This is used for importing commands
	 * from the "Header" to every page.
	 */
	importPage(engine: PageEngine)
	{
		// Commands are found in rawConfig.data as an array of lines
		// from the excel file.  So copy the ones from the source
		// to our list but at the start of our file.

		let list = [];
		for (let item of engine.rawConfig.data) list.push(item);

		let item = list.pop();
		while (item)
		{
			this.rawConfig.data.unshift(item);
			item = list.pop();
		}
	}

	/** Valid the "if" condition on a step
	If the condition does not exist true is returned.
	If the condition is valid (true) then true is returned.
	*/
	validateStepIfCondition(item: PageStepType): boolean
	{
		if (item.execute_if === undefined) return true;
		if (item.execute_if === null) return true;
		if (item.execute_if === "") return true;

		if (typeof (item.execute_if) !== "string")
		{
			if (LogicEngine.debugOutput)
				console.log("PageEngine validateStepIfCondition line ", item._row, " invalid type of:", item.execute_if);
			return true;
		}

		if (LogicEngine.debugOutput)
			console.log("PageEngine validateStepIfCondition line ", item._row, " evaluate:", item.execute_if);

		let condition = this.logic.processTextReplacement(item.execute_if.trim());

		// Process that condition into a boolean
		const result = this.logic.process(condition);

		if (LogicEngine.debugOutput)
			console.log("PageEngine validateStepIfCondition line ", item._row, " Condition:", condition, " result=", result);

		// Use the boolean formatter to convert possible text values
		// into boolean values
		let df = new DataFormatter();
		const logicalResult = df.unformatData('boolean', result);

		return logicalResult;
	}

	/**
	 * Process the steps from this page
	 * Fires the Initialize Page event then events for each element as they are processed.
	 */
	async processPage(pageRenderer: PageRenderBase)
	{
		await this.logic.initialize();

		//  Set references for the current page running
		LogicEngine.currentLineNumber = 0;
		LogicEngine.currentCommandText = "Starting";

		if (this.sendUpdates)
		{
			RemoteAgent.instance().startRemoteAgent();
			RemoteAgent.instance().currentPage = this;
			RemoteAgent.instance().pageStatus(LogicEngine.currentPageShowing, 'start');
		}

		this.renderer = pageRenderer;
		this.renderer.logicRef = this.logic;

		// Send initialization event
		this.evInitializePage.emit(this.rawConfig.meta);

		// Remove any previous conditions
		this.logic.conditions.resetConditions();

		// Process the page
		await this.processPageItems(this.rawConfig.data);

		await this.processEndOfPage(this.rawConfig.data);

		// End with a finished event
		this.evFinishedPage.emit(this.rawConfig.meta);

		// Notify the remote agent if connected
		if (this.sendUpdates)
			RemoteAgent.instance().pageStatus(LogicEngine.currentPageShowing, 'finished');
	}

	/** Notification Commands
	See StepNotification.ts for examples
	*/
	async stepNotification(step: PageStepType): Promise<boolean>
	{
		let options = new StringOptions(step.step_option);
		if (options.checkOption('Email'))
		{
			//  siteId: string, template: string, subject: string, data: any, allow_dupe: Boolean = false, participantId?: string
			let messageText = this.logic.processTextReplacement(step.text_en);
			let messageBody = this.logic.processTextReplacement(step.option_1);
			let messageReason = this.logic.processTextReplacement(step.option_2);
			let pid = this.logic.processTextReplacement(step.field_id);
			let result = await this.notificationManager.sendEmailToParticipant(pid, messageText, messageBody, messageReason);
			if (result)
			{
				this.logic.import(result);
			}
		} else if (options.checkOption('SiteEmail'))
		{
			//  siteId: string, template: string, subject: string, data: any, allow_dupe: Boolean = false, participantId?: string
			let messageText = this.logic.processTextReplacement(step.text_en);
			let messageBody = this.logic.processTextReplacement(step.option_1);
			let messageReason = this.logic.processTextReplacement(step.option_2);
			let site_id = this.logic.processTextReplacement(step.field_id);
			let result = await this.notificationManager.sendEmailToSite(site_id, messageText, messageBody, messageReason);
			if (result)
			{
				this.logic.import(result);
			}
		} else
			if (options.checkOption("Connect"))
			{
				await this.notificationManager.processConnect(step);
				return true;
			} else if (options.checkOption("Sms"))
			{
				let messageText = this.logic.processTextReplacement(step.text_en);
				let phoneNumber = this.logic.processTextReplacement(step.option_1);
				let messageReason = this.logic.processTextReplacement(step.option_2);
				let pid = this.logic.processTextReplacement(step.field_id);
				let result = await this.notificationManager.sendSmsToParticipant(pid, messageText, phoneNumber, messageReason);
				if (result)
				{
					this.logic.import(result);
				}
			} else if (options.checkOption("Send"))
			{
				let messageText = this.logic.processTextReplacement(step.text_en);
				let messageBody = this.logic.processTextReplacement(step.option_1);
				let messageReason = this.logic.processTextReplacement(step.option_2);
				let pid = this.logic.processTextReplacement(step.field_id);
				let result = await this.notificationManager.sendNotificaitonToParticipant(pid, messageText, messageBody, messageReason);
				if (result)
				{
					this.logic.import(result);
				}
			} else
				if (options.checkOption("Register") || options.checkOption("Subscribe"))
				{
					await this.notificationManager.processRegister(step);
					return true;
				} else
				{
					throw "processStep unknown option in Notification";
				}

		return true;
	}

	/**
	 * Text Event - Display text on the screen.
	 * Options:  Text
	 * @param step
	 */
	async stepText(step: PageStepType): Promise<boolean>
	{
		let obj = new StepTypes.Text(step);
		await obj.processStep(this.logic);
		this.renderer?.renderText(obj);
		return true;
	}

	/** Trace output event - Display text on the screen in trace mode only */
	async stepTrace(step: PageStepType): Promise<boolean>
	{
		let obj = new StepTypes.Text(step);
		await obj.processStep(this.logic);
		this.renderer?.renderTrace(obj, step._row || 0);
		return true;
	}

	async stepTask(step: PageStepType): Promise<boolean>
	{
		let obj = new StepTypes.Task(step);
		await obj.processStep(this.logic);
		return true;
	}

	async stepAudit(step: PageStepType): Promise<boolean>
	{
		let obj = new StepTypes.Audit(step);
		await obj.processStep(this.logic);
		return true;
	}

	async stepButton(step: PageStepType): Promise<boolean>
	{
		let obj = new StepTypes.Button(step);
		await obj.processStep(this.logic);
		this.renderer?.renderButton(obj);
		return true;
	}

	/** Imports records from a data source into a chart */
	async stepBeginData(step: PageStepType): Promise<boolean>
	{
		this.currentDataLoop = new StepTypes.Loop(step);

		if (!this.currentChart)
			throw "Unable to call stepBeginData with an active chart";

		this.currentChart.beginDataSet();

		return true;
	}

	async stepEndData(step: PageStepType): Promise<boolean>
	{
		if (!this.currentDataLoop)
			throw "Unable to end data without a current loop";

		this.currentDataLoop.evRecordCallback = async (record: DataRecord) =>
		{
			if (this.currentChart)
			{
				this.logic.import(record.data);
				this.currentChart.importDataToSet();
			} else
			{
				console.log("MISSING DATA CHART:", record);
			}

			return null;
		};

		await this.currentDataLoop.processStep(this.logic);
		await this.currentDataLoop.processEndLoop(this.logic);
		this.currentChart?.endDataSet();

		return true;
	}

	async stepMapData(step: PageStepType): Promise<boolean>
	{
		if (!this.currentChart)
			throw "Unable to call MapData with an active chart";

		this.currentChart.addDataMap(step);
		return true;
	}

	/** Begin a named chart where the type is in the option column */
	async stepBeginChart(step: PageStepType): Promise<boolean>
	{
		if (this.currentChart !== undefined) await this.stepEndChart(this.currentChart.raw);
		this.currentChart = new StepChart(step);
		await this.currentChart.processStep(this.logic);
		this.renderer?.renderChartStart(this.currentChart);
		this.currentChartActive = true;
		return true;
	}

	async stepEndChart(step: PageStepType): Promise<boolean>
	{
		this.currentChartActive = false;
		if (this.currentChart !== undefined)
		{
			await this.currentChart.processEndChart(step);
			this.renderer?.renderChartEnd(this.currentChart);
		}

		this.currentChart = undefined;
		return true;
	}

	async stepChartOption(step: PageStepType): Promise<boolean>
	{
		if (!this.currentChart)
		{
			console.error("ChartOptions called outside of a chart");
			return false;
		}

		await this.currentChart.addOption(step);
		return true;
	}

	async stepStartList(step: PageStepType): Promise<boolean>
	{
		if (this.currentList !== undefined) await this.stepEndList(this.currentList.raw);

		this.currentList = new StepTypes.List(step);
		await this.currentList.processStep(this.logic);

		this.renderer?.renderListStart(this.currentList);
		this.currentListActive = true;

		return true;
	}

	async stepEndList(step: PageStepType): Promise<boolean>
	{
		this.currentListActive = false;
		if (this.currentList !== undefined)
		{

			/* Loop through each record */
			let record = this.currentList.getNextRecord();
			while (record)
			{

				this.logic.import(record.data);

				/** Now process the items from "Option" types */
				await this.processPageItems(this.currentListOptions);

				//  code before the list so you have a chance to modify things if needed
				this.renderer?.renderListItemStart(this.currentList);

				/** Now process the items from the list */
				await this.processPageItems(this.currentListItems);

				this.renderer?.renderListItemEnd(this.currentList);

				// Get the next record in the list
				record = this.currentList.getNextRecord();
			}

			await this.currentList.processEndList(step);
			this.renderer?.renderListEnd(this.currentList);
		}

		this.currentListItems = [];
		this.currentListOptions = [];
		this.currentList = undefined;
		return true;
	}

	async stepListOption(step: PageStepType): Promise<boolean>
	{
		if (this.currentList !== undefined)
		{
			await this.currentList.addOption(step);
			return true;
		}

		if (this.currentTable !== undefined)
		{
			await this.currentTable.addOption(step);
			return true;
		}

		return true;
	}

	async stepGroupStart(step: PageStepType): Promise<boolean>
	{
		if (this.currentGroup !== undefined) await this.stepGroupEnd(this.currentGroup.raw);

		this.currentGroup = new StepTypes.Group(step);
		await this.currentGroup.processStep(this.logic);
		this.renderer?.startGroup(this.currentGroup);

		return true;
	}

	async stepStartSection(step: PageStepType): Promise<boolean>
	{
		if (this.currentSection) await this.stepEndSection(this.currentSection.raw);

		this.currentSection = new StepTypes.Section(step);
		await this.currentSection.processStep(this.logic);
		this.renderer?.startSection(this.currentSection);

		return true;
	}

	async stepEndSection(step: PageStepType): Promise<boolean>
	{
		// should not happen, this means we are closing a group
		// that is already closed
		if (!this.currentSection)
		{
			console.warn('PageEngine Warning: Ending a section without a begin section.');
			return true;
		}

		this.currentSection.options_end = step;
		this.currentSection.group_state = StepTypes.Section.SectionState.End;
		this.renderer?.endSection(this.currentSection);

		this.currentSection = undefined;
		return true;
	}

	async stepGroupEnd(step: PageStepType): Promise<boolean>
	{
		// should not happen, this means we are closing a group
		// that is already closed
		if (this.currentGroup === undefined) return true;

		this.currentGroup.options_end = step;
		this.currentGroup.group_state = StepTypes.Group.GroupState.End;
		this.renderer?.endGroup(this.currentGroup);

		this.currentGroup = undefined;
		return true;
	}

	async stepDashStat(step: PageStepType): Promise<boolean>
	{
		let dash = new StepTypes.DashStat(step);
		await dash.processStep(this.logic);
		this.renderer?.renderDashStat(dash);
		return true;
	}

	async stepDashButton(step: PageStepType): Promise<boolean>
	{
		let obj = new StepTypes.DashButton(step);
		await obj.processStep(this.logic);
		this.renderer?.renderDashButton(obj);
		return true;
	}

	async stepBreak(step: PageStepType): Promise<boolean>
	{
		let obj = new StepTypes.Break(step);
		await obj.processStep(this.logic);
		this.renderer?.renderBreak(obj);
		return true;
	}

	async stepSkipIf(step: PageStepType): Promise<boolean>
	{
		return true;
	}

	/** Increment a variable by 1 or the amount in step_option
	* Field name should be in Options or Text_En
	*/
	async stepLogicIncrement(step: PageStepType): Promise<boolean>
	{
		let fieldName = DataTools.internalValidateString(step.step_option);
		let fieldName2 = DataTools.internalValidateString(step.text_en);

		fieldName = this.logic.processTextReplacement(fieldName);
		fieldName = fieldName.replace(/ /g, "");
		fieldName = fieldName.trim();

		fieldName2 = this.logic.processTextReplacement(fieldName2);
		fieldName2 = fieldName2.replace(/ /g, "");
		fieldName2 = fieldName2.trim();

		let amount_str = DataTools.internalValidateString(step.option_1);
		let amount = DataTools.getNumber(this.logic.processTextReplacement(amount_str));
		if (!amount) amount = 1;

		if (LogicEngine.debugOutput)
			console.log("PageEngine stepLogicInc field=", fieldName, "amount=", amount);

		this.logic.fnIndexIncrement(fieldName, fieldName2, amount);
		return true;
	}

	async stepSetValue(step: PageStepType): Promise<boolean>
	{
		let obj = new StepSetValue(step);
		await obj.processStep(this.logic);
		return true;
	}

	async stepLookup(step: PageStepType): Promise<boolean>
	{
		let obj = new StepLookup(step);
		await obj.processStep(this.logic);
		return true;
	}

	async stepSet(step: PageStepType): Promise<boolean>
	{
		const field_id = step.field_id ? step.field_id : '';
		const value = step.option_1 ? this.logic.processTextReplacement(step.option_1) : '';
		this.logic.assign(field_id, value);
		return true;
	}

	/** Exit the current page early */
	async stepExit(step: PageStepType): Promise<boolean>
	{
		/// Dummy branch to eat up all the remaining commands
		this.branchManager.startBranch('RedirectImpossible', ['ImpossibleValue']);
		return false;
	}

	/**
	 * Redirect the current page to a different page.
	 * Can have "include" as option to include a page instead of redirect
	 * @param step
	 */
	async stepRedirect(step: PageStepType): Promise<boolean>
	{
		if (!step.text_en)
		{
			throw "Missing text_en on stepRedirect";
		}

		let pageRoute: string = step.text_en;
		pageRoute = this.logic.processTextReplacement(pageRoute);

		let pm = new PageManager();
		let newEngine = await pm.findRoute(pageRoute);
		if (newEngine && this.renderer)
		{
			// newEngine.evFinishedPage = this.evFinishedPage;
			newEngine.evForceRefresh = this.evForceRefresh;
			newEngine.evInitializePage = this.evInitializePage;
			newEngine.evStep = this.evStep;
			await newEngine.processPage(this.renderer);

			if (LogicEngine.debugOutput)
				console.log("PageEngine stepRedirect finished with redirect to page ", pageRoute);

			//  Special case, we can have the command "inlucde" in the options to avoid a full redirect
			const options = new StringOptions(step.step_option);
			if (!options.checkOption("include"))
			{
				/// Dummy branch to eat up all the remaining commands
				this.branchManager.startBranch('Redirect', ['ImpossibleValue']);
			}

			return true;
		}

		return false;
	}

	/**
	 * Include another page within this page
	 * the name of the page (route) should be in text
	 * optinally "silent" may be included in Step Option which will hide all output.
	 * @param step
	 * @example Include Option=Silent Text=/page/check/{{id}}  - Include a page as if called directly.  Has a unique logic engine, no output
	 * @example Include Option=Library Text=/page/library - Include a page, no output and no initialize events with shared logic engine.
	 * @example Include Option="" Text=/page/warning - Include a page in a unique logic engine with shared output
	 *
	 * This can be tested using /page/test_include in the Demo UAT site.
	 * Can also be tested in real-time using toolapitest's test_page.ts file.
	 */
	async stepInclude(step: PageStepType): Promise<boolean>
	{
		if (!step.text_en)
			throw "Missing text_en on stepInclude";

		let pageRoute: string = step.text_en;
		pageRoute = this.logic.processTextReplacement(pageRoute);

		let pm = new PageManager();
		pm.loadingAsLibrary = true;
		let newEngine = await pm.findRoute(pageRoute);
		if (newEngine && this.renderer)
		{
			newEngine.sendUpdates = false;  // Don't send remote page updates
			newEngine.evForceRefresh = this.evForceRefresh;
			newEngine.evStep = this.evStep;

			const options = new StringOptions(step.step_option);

			/** The "Library" option is used to include sub-functions or Macros or something that you want to execute
			in the shared LogicEngine that the current page is running in.  This is the same as if the commands where placed
			in the spot of the include command */
			if (options.checkOption("library"))
			{
				newEngine.evInitializePage.subscribe((metaData) =>
				{
					// Don't forward the evInitialPage event for included pages
					if (LogicEngine.debugOutput)
						console.log("PageEngine stepInclude evInitializePage:", metaData);
				});

				//  Remove the logic engine that was created and assign a reference to this one.
				//  Same for the macro list so that we can call macros created in the include file.
				//  Become some values, specificly page values such as /:#variable may have been
				//  set in logic engine already, force copy them to this page.
				for (const key of Object.keys(newEngine.logic.values))
				{
					if (typeof (newEngine.logic.values[key]) === "function")
						continue;
					if (!this.logic.values[key])
					{
						this.logic.assign(key, newEngine.logic.values[key]);
					}
				}

				newEngine.logic = this.logic;
				newEngine.macroList = this.macroList;
				newEngine.logic.assign("is_library", true);
				newEngine.logic.assign("is_silent", false);
			} else
			{
				newEngine.evInitializePage = this.evInitializePage;
				newEngine.logic.assign("is_library", false);
				newEngine.logic.assign("is_silent", false);
			}

			if (options.checkOption("silent"))
			{
				const silent = new PageRenderSilent();
				newEngine.logic.assign("is_silent", true);
				await newEngine.processPage(silent);
			} else
			{
				await newEngine.processPage(this.renderer);
			}

			return true;
		} else
		{
			console.error("PageEngine stepInclude unable to find route:", pageRoute);
		}

		return false;
	}

	async stepParticipant(step: PageStepType): Promise<boolean>
	{
		/* Loads the current participant record just like Record Load/Record Start
		*/

		// UUID To Load
		const uuid = DataTools.internalValidateString(this.logic.process("uuid"));

		// Value for Set command
		const value = this.logic.processTextReplacement(step.option_1);

		/* Command is always in the "Step Option" column */
		let command: StringOptions = new StringOptions(step.step_option);

		// TODO: What if a current record already exists that is not a participant?
		if (!this.currentRecord || this.currentRecord.tableName != 'Participant')
		{
			this.currentRecord = new StepTypes.Record(step, this.logic);
			this.currentRecord.record = new DataRecord('', {});
			if (await this.loadRecord("Participant", uuid))
			{
				this.currentRecord.record = this.currentRecords["Participant"];
				this.currentRecord.tableName = "Participant";
			}
		}

		//  Check for available commands
		if (command.checkOption('Set'))
		{
			const fieldName = DataTools.internalValidateString(step.field_id);
			if (this.currentRecord && this.currentRecord.record)
			{
				if (LogicEngine.debugOutput)
					console.log("PageEngine Participant Set Field=", fieldName, " Value=", value);

				this.currentRecord.record.data[fieldName] = value;
			}

			this.logic.assign(fieldName, value);
		}

		// Enable auto-save on a timer
		if (this.participantSaveTimer)
			clearTimeout(this.participantSaveTimer as number);

		// Wait some amount of time and auto-save the current record but don't clear
		// incase we have additional adjustments.
		this.participantSaveTimer = setTimeout(() =>
		{
			if (this.currentRecord && this.currentRecord.record)
			{
				if (LogicEngine.debugOutput)
					console.log("PageEngine Autosave Participant Record:", this.currentRecord.record.data);

				this.currentRecord.record.fixRecord()
					.then(() =>
					{
						// Should already be valid because of assign commands but import anyway if
						// fixrecord changed anything.
						if (this.currentRecord && this.currentRecord.record)
						{
							this.logic.import(this.currentRecord.record.data)
							this.currentRecord.record.saveIfNeeded();
							this.evForceRefresh.emit({ is_refresh_counts: true, reason: 'Record Save' });
						}
					})
			}

		}, this.participantAutosaveTime);

		return true;
	}

	/**
	 * Process a "Record" command until the stepRecord process
	 * function returns "false".  In that case, that is the end
	 * of the current record and we start a new one on the next
	 * record command.
	 */
	async stepRecord(step: PageStepType): Promise<boolean>
	{
		if (!this.currentRecord) this.currentRecord = new StepTypes.Record(step, this.logic);

		/* Value is always specified in the "option 1" column */
		const value = this.logic.processTextReplacement(step.option_1);

		/* Command is always in the "Step Option" column */
		let command: StringOptions = new StringOptions(step.step_option);

		if (command.checkOption('Start'))
		{
			/* Start a new record either way */
			this.currentRecord = new StepTypes.Record(step, this.logic);
			this.currentRecord.record = new DataRecord('', {});
		} else if (command.checkOption('LoadOrCreate'))
		{
			/*  Load a record or create one if the given ID is not found or the
			record with the matching ID was not found. */
			const tableName = step.field_id ? step.field_id : '';

			if (!this.currentRecord)
			{
				this.currentRecord = new StepTypes.Record(step, this.logic);
			}

			if (value && await this.loadRecord(tableName, value))
			{
				this.currentRecord.record = this.currentRecords[tableName];
				this.currentRecord.tableName = '';
			} else
			{
				this.currentRecord.record = new DataRecord(tableName, {});
				this.currentRecord.record.id = value;
				this.currentRecord.record.data.id = value;
			}

		} else if (command.checkOption('Load'))
		{
			/* Load a record using page engine so it can't be done inside the
			step iteself */
			const tableName = step.field_id ? step.field_id : '';

			if (tableName === '')
			{
				/* Table name is blank so check to see if this is an API Call */
				let path = step.text_en ? step.text_en : '';
				if (/\/cdn\//.test(path))
				{
					let method = step.option_1 ? step.option_1 : 'file';
					await this.loadCdnIntoRecord(path, method);
				} else
					if (/api/.test(path))
					{
						let method = step.option_1 ? step.option_1 : 'get';
						await this.loadApiIntoRecord(path, method);
					}
			} else if (await this.loadRecord(tableName, value))
			{
				this.currentRecord.record = this.currentRecords[tableName];
				this.currentRecord.tableName = '';
			} else
			{
				throw 'Exception in stepRecord, unable to load table=' + tableName + ' id=' + value;
			}
		} else if (command.checkOption('Set'))
		{
			const fieldName = step.field_id ? step.field_id : '';
			if (this.currentRecord && this.currentRecord.record)
			{
				if (LogicEngine.debugOutput)
					console.log("PageEngine Record Set Field=", fieldName, " Value=", value);
				this.currentRecord.record.data[fieldName] = value;
			}

			this.logic.assign(fieldName, value);
		} else if (command.checkOption('Import'))
		{
			const fieldName = step.field_id ? step.field_id : '';
			let option = step.option_1 ? step.option_1 : '';
			if (!option || option.length === 0) option = fieldName;

			if (this.currentRecord && this.currentRecord.record)
			{
				this.logic.assign(fieldName, this.currentRecord.record.data[option]);
			}

			// process.exit();
		} else if (command.checkOption('Save'))
		{

			if (this.currentRecord && this.currentRecord.record)
			{
				this.currentRecord.record.validateId();
				await this.currentRecord.record.fixRecord();
				if (LogicEngine.debugOutput)
					console.log("PageEngine Record Save:", this.currentRecord.record.data);
				this.logic.import(this.currentRecord.record.data)
				if (!(await this.currentRecord.record.saveIfNeeded()))
				{
					console.warn('PageEngine stepRecord failed to save record', this.currentRecord);
				}

				try
				{
					/** Try to save an audit record for a Record Save event */
					if (!this.currentRecord.tableName.startsWith("#"))
					{
						const participantId = LogicEngine.currentParticipantID || this.logic.values["participant_id"];
						const auditText = (step._row || "") + ": Record Save values = " + JSON.stringify(this.currentRecord.record.data);
						const audit = new AuditLog(participantId, auditText);
						audit.setLinkedRoute(LogicEngine.currentPageShowing);
						audit.setTableName(this.currentRecord.tableName, this.currentRecord.record.data.id);
						audit.setEventType(AuditLog.EventTypes.FieldChange);
						await audit.save();
					}
				} catch (e)
				{
				}

			}

			this.currentRecord = undefined;
			this.evForceRefresh.emit({ is_refresh_counts: true, reason: 'Record Save' });
		} else
		{
			console.warn('PageEngine stepRecord unknown command:', command, ' for step', step);
		}

		return true;
	}

	async stepOneTest(step: PageStepType): Promise<boolean>
	{
		// Check to see if there is an "if" conditions for the step, don't process steps that
		// fail to check the if condition.
		if (!this.validateStepIfCondition(step))
			return true;

		if (this.currentTest)
			this.currentTest.addStep(step);

		return true;
	}

	async stepBeginTest(step: PageStepType): Promise<boolean>
	{
		this.branchManager.startBranch('Test', ['endtest']);
		this.currentTest = new StepTypes.Test(step);
		await this.currentTest.processStep(this.logic);
		return true;
	}

	async stepEndTest(step: PageStepType): Promise<boolean>
	{
		if (this.currentTest)
		{
			const branch = this.branchManager.endBranch();
			this.currentTest.testSteps.push(...branch.commands);
			await this.currentTest.processEndTest(this.logic);
			this.renderer?.renderTestCase(this.currentTest);
		}

		this.currentLoop = undefined;
		return true;
	}


	/** Begin a loop.  Similar to a table except there is no output */
	async stepBeginLoop(step: PageStepType): Promise<boolean>
	{
		this.branchManager.startBranch('Loop', ['endloop', 'loopend']);
		this.currentLoop = new StepTypes.Loop(step);
		await this.currentLoop.processStep(this.logic);
		return true;
	}


	/** End a loop command, execute the loop contents */
	async stepEndLoop(step: PageStepType): Promise<boolean>
	{
		if (this.currentLoop)
		{
			const branch = this.branchManager.endBranch();
			if (branch.commands.length > 0)
			{
				this.currentLoop.evRecordCallback = async (record: DataRecord) =>
				{
					await this.processPageItems(branch.commands);
					return null;
				};
			}

			await this.currentLoop.processEndLoop(this.logic);
		}

		this.currentLoop = undefined;
		return true;
	}

	async stepBeginTable(step: PageStepType): Promise<boolean>
	{
		this.branchManager.startBranch('Table', ['endtable', 'tableend']);
		this.currentTable = new StepTypes.Table(step);
		await this.currentTable.processStep(this.logic);
		return true;
	}

	async stepEndTable(step: PageStepType): Promise<boolean>
	{
		if (this.currentTable)
		{
			const branch = this.branchManager.endBranch();
			if (branch.commands.length > 0)
			{
				this.currentTable.evRecordCallback = async (record: DataRecord) =>
				{
					await this.processPageItems(branch.commands);
					return null;
				};
			}

			await this.currentTable.processEndTable(this.logic);
			this.renderer?.renderTable(this.currentTable);
		}

		this.currentTable = undefined;
		return true;
	}


	async stepColumn(step: PageStepType): Promise<boolean>
	{
		if (!this.currentTable) return false;
		this.currentTable.addColumn(step);
		return true;
	}

	async stepBeginTabs(step: PageStepType): Promise<boolean>
	{
		this.currentTabs = new StepTypes.Tabs(step);
		await this.currentTabs.processStep(this.logic);
		return true;
	}

	async stepEndTabs(step: PageStepType): Promise<boolean>
	{
		if (!this.currentTabs) return false;
		this.renderer?.renderTabs(this.currentTabs);
		return true;
	}

	async stepTab(step: PageStepType): Promise<boolean>
	{
		if (!this.currentTabs) return false;
		this.currentTabs.addTab(step);
		return true;
	}

	async stepBeginWindow(step: PageStepType): Promise<boolean>
	{
		this.currentWindow = new StepTypes.Window(step);
		await this.currentWindow.processStep(this.logic);
		return true;
	}

	async stepEndWindow(step: PageStepType): Promise<boolean>
	{
		if (!this.currentWindow) return false;
		this.renderer?.renderWindow(this.currentWindow);
		return true;
	}

	async stepWindow(step: PageStepType): Promise<boolean>
	{
		if (!this.currentWindow) return false;
		this.currentWindow.addWindow(step);
		return true;
	}

	async stepBeginForm(step: PageStepType): Promise<boolean>
	{
		this.currentForm = new StepTypes.Form(step);
		this.currentForm.evOnSave.subscribe((errors: FieldError[]) =>
		{
			// Form was saved which means we may need to update values on some badge counts
			this.evForceRefresh.emit({ is_refresh_counts: true, reason: 'Form save' });
		});

		await this.currentForm.processStep(this.logic);
		if (this.renderer)
		{
			this.renderer.formOptRef = this.currentForm;
			this.renderer.renderFormStart(this.currentForm);
		} else
		{
			console.warn("stepBeginForm Warning, should have renderer set and we don't.");
		}

		return true;
	}

	async stepEndForm(step: PageStepType): Promise<boolean>
	{
		if (!this.currentForm) return false;

		if (this.renderer)
		{
			this.renderer.formOptRef = this.currentForm;
			this.renderer.renderFormEnd(this.currentForm);

			/* Give the engine time to render and then update conditions for null conditions that
			would be triggered on a new form */
			setTimeout(() =>
			{
				this.logic.runConditions();
			}, 250);

			/** Special case for Data Monitor role, output a notice on the bottom of the page */
			if (this.logic.fnHasGroup('DataMonitor'))
			{
				await this.processPageItems([
					{
						step_type: 'Text',
						step_option: 'AlertWarning',
						text_en:
							'You are currently logged in as a Data Monitor and can not save forms.',
					},
				]);
			}
		} else
		{
			console.warn("StepEndForm Warning, should have renderer set and we don't.");
		}

		return true;
	}

	/**
	 * Conditional
	 * Step Option - A supported command
	 *      FieldEnabled - Updates the status of a field based on the status of a condition
	 * Text - The condition for logic engine to procss
	 * Field Id - The field affectd by the condition
	 *
	 * @example
	 * Conditional FieldEnabled {{ age > 60 }} old_person_field
	 * Conditional Validation "Error text" "Condition"
	 *
	 */
	async stepConditional(step: PageStepType): Promise<boolean>
	{
		let cond = new StepTypes.Conditional(step);
		await cond.processStep(this.logic);

		if (cond.options.checkOption('Validation'))
		{
			if (this.currentForm)
			{
				cond.conditionalType = 'error';
				this.currentForm.addCondition(cond);
				return true;
			}
		}

		if (cond.options.checkOption('Score'))
		{
			if (this.currentForm)
			{
				this.currentForm.addScoring(cond);
				return true;
			}
		}

		if (cond.options.checkOption('Warning'))
		{
			if (this.currentForm)
			{
				cond.conditionalType = 'warning';
				this.currentForm.addCondition(cond);
				return true;
			}
		}

		this.logic.conditions.addCondition(cond);
		return true;
	}

	async stepField(step: PageStepType): Promise<boolean>
	{
		if (!this.currentForm) return false;

		let field = await this.currentForm.addField(step);
		if (field && this.renderer)
		{
			this.renderer.renderFormField(this.currentForm, field);

			if (field.enabled !== undefined && field.enabled === false)
			{
				// setTimeout((field) => {
				//     console.log("PageEngine evFieldChange For Disable Field ", field.field);
				//     this.logic.evFieldChanged.emit({
				//         field: field.field,
				//         eventType: "FieldChange",
				//         enabled: false
				//     });
				// }, 500, field);
			}
		}

		return true;
	}

	async stepDefault(step: PageStepType): Promise<boolean>
	{
		console.log('Unkown step found [', step.step_type, '] = ', step);
		this.renderer?.renderUnknown(step);
		return true;
	}

	async startConditionalElse(step: PageStepType): Promise<boolean>
	{
		const branch = this.branchManager.endBranch();
		if (branch.userData && branch.userData === true)
		{
			// Logical if condition has been met
			// console.log("-- LOGIC ELSE RUNNING BRANCH:", branch.commands);
			await this.processPageItems(branch.commands);
			// Start a branch so we can collect code until the end if
			this.branchManager.startBranch('Else Statement', ['endif'], false);
		} else
		{
			// Start a branch so we can collect code until the end if
			this.branchManager.startBranch('Else Statement', ['endif'], true);
		}
		return true;
	}

	/** Handle end of if statement "end if" by processing the branch
	 * if the stored logical condition was true
	 */
	async endConditionalIf(step: PageStepType): Promise<boolean>
	{
		const branch = this.branchManager.endBranch();
		if (branch.userData && branch.userData === true)
		{
			// Logical if condition has been met
			// console.log("-- LOGIC ENDIF RUNNING BRANCH:", branch.commands);
			await this.processPageItems(branch.commands);
		}
		return true;
	}

	async startConditionalIf(step: PageStepType): Promise<boolean>
	{
		// Convert the text field into a result using logic engine
		const condition = this.logic.processTextReplacement(step.text_en);

		// Process that condition into a boolean
		const result = this.logic.process(condition);

		// console.log("-- IMPORTS:", this.logic.values);
		// Use the boolean formatter to convert possible text values
		// into boolean values
		let df = new DataFormatter();
		const logicalResult = df.unformatData('boolean', result);

		if (LogicEngine.debugOutput)
			console.log("PageEngine IF Condition [", step.text_en, "] running [", condition, "] = ", result);

		// Start a branch so we can collect code until the end if
		this.branchManager.startBranch('IF Statement', ['endif', 'else'], logicalResult);

		return true;
	}

	/** Comment in the file, no other columns matter
	 *
	 */
	async stepComment(step: PageStepType): Promise<boolean>
	{
		return true;
	}

	/** Enable or disable logic/page engine output */
	async stepDebug(step: PageStepType): Promise<boolean>
	{
		if (step.step_option && typeof (step.step_option) === "string")
		{
			if (step.step_option.trim().toLowerCase() === "off")
				LogicEngine.debugOutput = false;
			if (step.step_option.trim().toLowerCase() === "stop")
				LogicEngine.debugOutput = false;
		} else
		{
			LogicEngine.debugOutput = true;
			console.log("PlatformShared Debugging Enabled");
		}

		return true;
	}

	/** Define a block of code and give it a name.  The name of the code block should be in
	  * The "Option" field.  This is similar to a macro except that the code is passed to a render function.
	 */
	async internalBeginCode(step: PageStepType): Promise<boolean>
	{
		let name = step.step_option ? step.step_option : '';
		if (name === '')
		{
			console.warn('PageEngine StartCode invalid, missing name in step_option column.');
			name = "TempCode";
		}

		this.branchManager.startBranch(name, ['endcode']);
		return true;
	}

	/** When a macro ends we copy the commands that were saved in the branch to the current chain */
	async internalEndCode(step: PageStepType): Promise<boolean>
	{
		const branch = this.branchManager.endBranch();
		if (branch.name != "TempCode" && !branch.name.startsWith("#"))
		{
			// Copy the commands to a macro by the same name.
			this.macroList[branch.name] = branch.commands;
		}

		if (this.renderer)
			this.renderer.renderCodeBlock(step, branch.name, branch.commands);

		return true;
	}

	/** Define a macro named in the step_option column which can be used
	 * later.  This simply holds back commands and doesn't execute them
	 * until they are referenced with the Macro command.
	 */
	async internalBeginMacro(step: PageStepType): Promise<boolean>
	{
		let name = step.step_option ? step.step_option : '';
		if (name === '')
		{
			console.warn('PageEngine StartMacro invalid, missing name in step_option column.');
			return false;
		}

		this.branchManager.startBranch(name, ['endmacro', 'stopmacro']);
		return true;
	}

	/** When a macro ends we copy the commands that were saved in the branch to the current chain */
	async internalEndMacro(step: PageStepType): Promise<boolean>
	{
		const branch = this.branchManager.endBranch();
		this.macroList[branch.name] = branch.commands;
		return true;
	}

	async internalRunMacro(step: PageStepType): Promise<boolean>
	{
		let name = step.step_option ? step.step_option : '';
		if (name === '')
		{
			console.warn('PageEngine StartMacro invalid, missing name in step_option column.');
			return false;
		}

		if (step.field_id && typeof (step.field_id) === "string")
			this.logic.assign("field_id", this.logic.processTextReplacement(step.field_id));

		if (step.option_1 && typeof (step.option_1) === "string")
			this.logic.assign("option_1", this.logic.processTextReplacement(step.option_1));

		if (step.option_2 && typeof (step.option_2) === "string")
			this.logic.assign("option_2", this.logic.processTextReplacement(step.option_2));

		if (this.macroList[name])
		{
			await this.processPageItems(this.macroList[name]);
		} else
		{
			console.warn("PageEngine Macro can't find name:", name);
		}

		return true;
	}

	/** Import records from a source into the page */
	async stepImport(step: PageStepType): Promise<boolean>
	{
		const stepLoop = new StepLoop(step);
		await stepLoop.processStep(this.logic);
		await stepLoop.processEndLoop(this.logic);
		return true;
	}

	async internalBeginLoop(step: PageStepType): Promise<boolean>
	{
		let name = step.step_option ? step.step_option : '';
		if (name === '')
		{
			console.warn('PageEngine StartMacro invalid, missing name in step_option column.');
			return false;
		}

		//  Common options and default
		let loopData: stepLoopData = {
			loopType: 'counter',
			condition: step.text_en ? step.text_en : '',
			variableName: step.field_id ? step.field_id.trim() : 'counter'
		};

		const options = new StringOptions(step.step_option);
		if (options.checkOption("List"))
		{
			loopData.loopType = 'list';
		} else
		{
			// counter data by default
			loopData.startValue = DataTools.getNumber(name) ?? 0;
			loopData.increase = DataTools.getNumber(step.field_id) ?? 1;
			loopData.variableName = 'counter';

			this.logic.assign('counter', loopData.startValue);
			this.logic.assign('increase', loopData.increase);
		}

		this.branchManager.startBranch(name, ['endloop'], loopData);
		return true;
	}

	async internalEndLoop(step: PageStepType): Promise<boolean>
	{
		const branch = this.branchManager.endBranch();
		const increase = +this.logic.getVariable('increase');
		// count the loop
		let count = 0;
		// use dataformatter
		let df = new DataFormatter();

		const data = (branch.userData as stepLoopData);
		if (data.loopType === "list")
		{
			let items = this.logic.processTextReplacement(data.condition).split(',');
			let counter = 0;
			for (const item of items)
			{
				let value = item.trim();
				this.logic.assign(data.variableName, value);
				this.logic.assign("counter", counter++);
				await this.processPageItems(branch.commands);
			}
		} else
		{
			// Counter
			while (df.unformatData('boolean', this.logic.processTextReplacement(data.condition)))
			{
				await this.processPageItems(branch.commands);
				// update counter
				let counter: any = this.logic.getVariable('counter');
				if (typeof (counter) === "string") counter = parseInt(counter, 10);

				this.logic.assign('counter', counter + data.increase);
				// break if the loop continues more than 500 times
				count++;
				if (count > 500) break;
			}
		}


		return true;
	}
}
